import { CheckCircle2, Upload } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import type { AssetsImportReport } from '../../hooks/useAssets';

/**
 * Result panel for the asset CSV import: the per-outcome tallies, any
 * per-row errors, and the commit button for a clean dry run. Split out of
 * ImportAssets to keep that file under the size cap.
 */
export function ImportAssetsReportPanel({
  report,
  onCommit,
}: {
  report: AssetsImportReport;
  onCommit: () => void;
}) {
  return (
    <section className="mt-4 rounded-lg border border-line/10 bg-surface p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          {report.dry_run ? 'Dry-run report' : 'Import complete'}
        </h2>
        <span className="text-xs text-fg-muted">
          {report.total} row{report.total === 1 ? '' : 's'} processed
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <Stat
          label={report.dry_run ? 'Would insert' : 'Inserted'}
          value={report.inserted}
          tone="emerald"
        />
        <Stat
          label={report.dry_run ? 'Would assign' : 'Pre-assigned'}
          value={report.assigned ?? 0}
          tone="sky"
        />
        <Stat
          label="Confirm on arrival"
          value={report.to_confirm ?? 0}
          tone="sky"
        />
        <Stat
          label={report.dry_run ? 'Syscodes to update' : 'Syscodes updated'}
          value={report.syscode_updated ?? 0}
          tone="sky"
        />
        <Stat label="Skipped (duplicate tag)" value={report.skipped} tone="slate" />
        <Stat label="Errors" value={report.errors.length} tone="red" />
      </div>
      {report.errors.length > 0 ? (
        <ul className="mt-4 max-h-64 space-y-1 overflow-y-auto rounded-md bg-danger-soft/15 px-3 py-2 text-xs text-danger-soft-fg">
          {report.errors.map((e) => (
            <li key={`${e.row}-${e.message}`}>
              <span className="font-mono">row {e.row}:</span> {e.message}
            </li>
          ))}
        </ul>
      ) : null}
      {report.dry_run && report.errors.length === 0 ? (
        <div className="mt-4 flex justify-end">
          <Button
            variant="primary"
            size="md"
            iconLeft={<Upload className="h-4 w-4" />}
            onClick={onCommit}
          >
            Commit import
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'emerald' | 'slate' | 'red' | 'sky';
}) {
  const cls = {
    emerald: 'bg-success-soft/15 text-success-soft-fg',
    slate: 'bg-surface-2 text-fg',
    red: 'bg-danger-soft/15 text-danger-soft-fg',
    sky: 'bg-info-soft/15 text-info-soft-fg',
  }[tone];
  return (
    <div className={`rounded-md px-3 py-2 ${cls}`}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-75">{label}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
